import type { SupervisorAuditSampleItem } from '@avisoma/shared';
import { readApiErrorMessage } from '../../lib/apiErrorMessage';
import { formatValue } from '../../lib/formatValue';

export const AUDIT_SHORTCUT_HINT =
  'Keys: 1/2 real discrepancy · 3/4/5 proposed value · 6/7 useful · Enter submits (not in the note box)';

const confidenceText = (c: number | null): string => (c === null ? 'confidence n/a' : `confidence ${Math.round(c * 100)} %`);

/**
 * The judge line: the latest attempt when there is one; else "rule-only" for a rule finding; else the finding's own
 * stored verdict, marked as having no attempt history (judged before phase 5 recorded attempts).
 */
export const verdictLine = (item: SupervisorAuditSampleItem): string => {
  const a = item.latestAttempt;
  if (a) return `${a.verdict} · ${confidenceText(a.confidence)} · ${a.model}`;
  const f = item.finding;
  if (f.model === null || f.model === 'rule') return 'rule-only';
  return `${f.status} · ${confidenceText(f.confidence)} · ${f.model} (no attempt history)`;
};

export type RowEntry = { key: string; value: string; highlighted: boolean };

/** The extracted row as display pairs, with the finding's field marked. */
export const rowEntries = (row: Record<string, unknown>, field: string): RowEntry[] =>
  Object.entries(row).map(([key, v]) => ({ key, value: formatValue(v), highlighted: key === field }));

/** "3 / 20 labelled"; skipped items are not labelled, so they are counted apart: "3 / 20 labelled, 2 skipped". */
export const progressLabel = (labelled: number, size: number, skipped = 0): string =>
  skipped > 0 ? `${labelled} / ${size} labelled, ${skipped} skipped` : `${labelled} / ${size} labelled`;

/** `empty`: the sample had nothing to label; `done`: every item is labelled or skipped; else the current item. */
export const sessionView = (position: number, size: number): 'empty' | 'item' | 'done' =>
  size === 0 ? 'empty' : position >= size ? 'done' : 'item';

/** Where the reviewer is in the sample (`position`) and how many items they labelled; the rest up to it were skipped. */
export type AuditProgress = { position: number; labelled: number };

export const START_PROGRESS: AuditProgress = { position: 0, labelled: 0 };

/** After a successful submit of the item at `submittedAt`: a late duplicate success does not skip ahead. */
export const afterLabel = (p: AuditProgress, submittedAt: number): AuditProgress =>
  p.position === submittedAt ? { position: p.position + 1, labelled: p.labelled + 1 } : p;

/** After a skip of the item at `skippedAt`, by the reviewer or for a finding that is gone. Once per item. */
export const afterSkip = (p: AuditProgress, skippedAt: number): AuditProgress =>
  p.position === skippedAt ? { ...p, position: p.position + 1 } : p;

export type SubmitErrorOutcome = { advance: boolean; note: string };

/**
 * What a failed submit does. 404: the finding was deleted after the sample was drawn, so move on. 409: a run moved
 * the finding to another check version after the sample was drawn, so the label would judge content the reviewer
 * did not see; move on. Anything else
 * (400: the label no longer fits the finding, for example a re-judge changed whether it has a proposal) keeps the
 * item and shows the server's reason; the reviewer can fix the answer or skip.
 */
export const submitErrorOutcome = (error: unknown): SubmitErrorOutcome => {
  const status =
    typeof error === 'object' && error !== null ? (error as { response?: { status?: number } }).response?.status : undefined;
  if (status === 404) return { advance: true, note: 'The finding no longer exists, so it was skipped.' };
  if (status === 409) return { advance: true, note: 'The finding changed since it was sampled, so it was skipped.' };
  const detail = readApiErrorMessage(error);
  const note = detail ? `Label not stored: ${detail}` : 'Label not stored. Submit again or skip this finding.';
  return { advance: false, note };
};
